import { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
    Smartphone, Copy, Check, X as XIcon, AlertTriangle, WifiOff, RefreshCw,
    ArrowRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { apiErrorMessage, translateErrorText } from "@/lib/apiErrorMessage";
import { toast } from "sonner";
import { useLatest } from "@/hooks/useLatest";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

/**
 * PhoneCompanionModal
 * -------------------
 * Renders the QR code + short URL for the Phone Companion Capture session,
 * subscribes to the desktop-side SSE stream, and calls back on capture.
 *
 * P0-B stability fixes (Feb 2026):
 *   - `restaurantId` is read from a ref, NOT from effect deps, so a
 *     transient AppContext re-render can no longer trigger a session
 *     recreation and orphan the phone.
 *   - Explicit states: `connecting` (POST /session in flight) → `waiting`
 *     (QR shown, phone hasn't hit /session-info yet) → `connected` →
 *     `reconnecting` (SSE dropped, EventSource auto-retries) → `offline`
 *     (still no reconnect after N seconds) → `ended` / `expired`.
 *   - The SSE stream is re-opened on `visibilitychange` when the desktop
 *     tab was backgrounded (some browsers close EventSource on hide).
 *   - Explicit "Reconnect" button so the user can trigger a fresh QR
 *     without closing and re-opening the dialog.
 *   - Backend TTL extended to 60 min with auto-extension on every phone
 *     heartbeat (see companion_service.py), so the "Session expired"
 *     surface disappears in practice unless the phone is truly idle.
 */

const RECONNECT_HARD_MS = 15_000;  // after this, escalate "reconnecting" → "offline"

export default function PhoneCompanionModal({
    open, mode, restaurantId, inventoryCountId, onClose, onCapture,
    onCaptureExtracted, onContinueToReview, onFinish, onSessionChange,
    title, fresh,
}) {
    const { t } = useTranslation();
    const [session, setSession] = useState(null);   // { session_id, short_code, capture_url }
    // status: initialising | waiting | connected | reconnecting | offline | ended | expired | error
    const [status, setStatus] = useState("initialising");
    const [error, setError] = useState("");
    const [captureCount, setCaptureCount] = useState(0);
    // Ordered list of captures received in this session (delivery mode).
    // Merged source: initial GET + SSE capture-added + periodic poll.
    const [captures, setCaptures] = useState([]);
    // 2026-02-27 Review Handoff — chef can multi-select extracted
    // captures and click "Continue to Review".  Default: nothing
    // selected; when a capture reaches "extracted" it becomes
    // selectable but is left unchecked so the chef makes an explicit
    // grouping decision (three photos for one supplier vs two
    // suppliers = two deliveries).
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [copied, setCopied] = useState(false);
    const [retryNonce, setRetryNonce] = useState(0);

    const esRef = useRef(null);
    const restaurantRef = useRef(restaurantId);
    const inventoryCountRef = useLatest(inventoryCountId);
    const onSessionChangeRef = useLatest(onSessionChange);
    const onFinishRef = useLatest(onFinish);
    const onCloseRef = useLatest(onClose);
    const reconnectTimerRef = useRef(null);
    // Track capture_ids we've already delivered upstream so neither an
    // SSE replay nor the poll fallback double-counts a capture.
    const seenCapturesRef = useRef(new Set());
    // Track capture_ids that have already fired onCaptureExtracted so a
    // re-poll after extraction completes does not re-notify.
    const notifiedExtractionRef = useRef(new Set());
    const pollRef = useRef(null);
    const onCaptureRef = useRef(onCapture);
    const onCaptureExtractedRef = useRef(onCaptureExtracted);
    useEffect(() => { onCaptureRef.current = onCapture; }, [onCapture]);
    useEffect(() => { onCaptureExtractedRef.current = onCaptureExtracted; }, [onCaptureExtracted]);

    // Keep the latest restaurantId available WITHOUT triggering the
    // session-creation effect below. This is the critical fix that stops
    // orphaning an already-connected phone every time AppContext
    // re-renders.
    useEffect(() => { restaurantRef.current = restaurantId; }, [restaurantId]);

    // Create the session when the modal opens or when the user explicitly
    // retries. `restaurantId` is intentionally NOT a dependency.
    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        setSession(null);
        setStatus("initialising");
        setError("");
        setCaptureCount(0);
        setCopied(false);
        setCaptures([]);
        setSelectedIds(new Set());
        seenCapturesRef.current = new Set();
        notifiedExtractionRef.current = new Set();
        (async () => {
            try {
                const body = { mode };
                const rid = restaurantRef.current;
                if (rid) body.restaurant_id = rid;
                // Phase 2.1 — bind session to the active inventory count so
                // the backend can enforce the "one item per product per
                // count" business rule.
                const countId = inventoryCountRef.current;
                if (countId) body.inventory_count_id = countId;
                // 2026-02-27 Review Handoff — retryNonce > 0 means the
                // user hit "Regenerate" so we ask the backend for a
                // brand-new session and cancel any prior one.  On a
                // plain (re)open we resume any still-alive session so
                // closing/reopening the modal preserves captures.
                if (retryNonce > 0 || fresh) body.force_new = true;
                const { data } = await api.post("/companion/session", body);
                if (cancelled) return;
                setSession(data);
                setStatus("waiting");
                if (onSessionChangeRef.current) onSessionChangeRef.current(data.session_id);
            } catch (err) {
                if (cancelled) return;
                setError(err.response ? apiErrorMessage(err) : err.message);
                setStatus("error");
            }
        })();
        return () => { cancelled = true; };
    }, [open, mode, retryNonce, fresh, inventoryCountRef, onSessionChangeRef]);

    // Open the SSE stream once the session is created. Recreates the
    // stream if the session id changes or the user re-tries.
    useEffect(() => {
        if (!session?.session_id) return undefined;
        const backend = process.env.REACT_APP_BACKEND_URL;
        const url = `${backend}/api/companion/stream/${session.session_id}`;
        let closed = false;

        const openStream = () => {
            if (closed) return;
            const es = new EventSource(url, { withCredentials: true });
            esRef.current = es;

            es.addEventListener("session", () => {/* snapshot only */});
            es.addEventListener("phone_connected", () => {
                if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
                setStatus("connected");
            });
            es.addEventListener("phone_disconnected", () => {
                // The phone stopped heartbeating — show "Reconnecting…"
                // (server auto-recovers when the phone comes back).
                setStatus((s) => (s === "ended" || s === "expired" ? s : "reconnecting"));
                if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = setTimeout(() => {
                    setStatus((s) => (s === "reconnecting" ? "offline" : s));
                }, RECONNECT_HARD_MS);
            });
            es.addEventListener("session_ended", (evt) => {
                let reason = "ended";
                try {
                    const payload = JSON.parse(evt.data);
                    reason = payload?.reason || "ended";
                } catch (_e) { /* ignore */ }
                setStatus(reason === "expired" ? "expired" : "ended");
                closed = true;
                es.close();
                // Phone pressed Finish: deliver any page the stream missed,
                // then hand back to the desktop page review.
                if (reason === "finished" && mode === "delivery" && onFinishRef.current) {
                    const sid = session?.session_id;
                    (async () => {
                        try {
                            const { data } = await api.get(`/companion/captures/${sid}`);
                            for (const cap of data?.captures || []) {
                                if (!cap.capture_id || seenCapturesRef.current.has(cap.capture_id)) continue;
                                seenCapturesRef.current.add(cap.capture_id);
                                if (onCaptureRef.current) onCaptureRef.current({ kind: "delivery", ...cap }, sid);
                            }
                        } catch (_e) { /* pages already received stay in the list */ }
                        setSession(null);
                        onFinishRef.current();
                        if (onCloseRef.current) onCloseRef.current();
                    })();
                }
            });
            es.addEventListener("capture", (evt) => {
                try {
                    const payload = JSON.parse(evt.data);
                    // Gap B dedup — the same barcode event may be
                    // delivered by SSE and by the periodic poll.
                    // Track event_id in the shared seenCapturesRef so
                    // downstream `onCapture` runs exactly once.
                    const eid = payload?.event_id;
                    if (eid) {
                        if (seenCapturesRef.current.has(eid)) return;
                        seenCapturesRef.current.add(eid);
                    }
                    setCaptureCount((c) => c + 1);
                    if (onCaptureRef.current) onCaptureRef.current(payload, session?.session_id);
                } catch (_e) { /* ignore */ }
            });
            // Delivery mode — durable append-only capture stream.
            // Backend emits `capture-added` for every photo persisted
            // (before AI extraction), and `capture-extracted` /
            // `capture-failed` for the async AI outcome. The desktop
            // dedupes by capture_id via seenCapturesRef.
            es.addEventListener("capture-added", (evt) => {
                try {
                    const payload = JSON.parse(evt.data);
                    const cid = payload?.capture_id;
                    if (!cid || seenCapturesRef.current.has(cid)) return;
                    seenCapturesRef.current.add(cid);
                    setCaptureCount((c) => c + 1);
                    setCaptures((list) => list.some((c) => c.capture_id === cid)
                        ? list
                        : [...list, { ...payload, status: payload.status || "uploaded" }]);
                    if (onCaptureRef.current) {
                        onCaptureRef.current(
                            { kind: "delivery", ...payload },
                            session?.session_id);
                    }
                } catch (_e) { /* ignore */ }
            });
            es.addEventListener("capture-extracted", (evt) => {
                try {
                    const payload = JSON.parse(evt.data);
                    const cid = payload?.capture_id;
                    if (!cid) return;
                    setCaptures((list) => list.map((c) => c.capture_id === cid
                        ? { ...c, status: "extracted",
                            extraction_summary: payload.summary }
                        : c));
                    if (!notifiedExtractionRef.current.has(cid)
                        && onCaptureExtractedRef.current) {
                        notifiedExtractionRef.current.add(cid);
                        onCaptureExtractedRef.current(
                            { capture_id: cid,
                              status: "extracted",
                              extraction_summary: payload.summary },
                            session?.session_id);
                    }
                } catch (_e) { /* ignore */ }
            });
            es.addEventListener("capture-failed", (evt) => {
                try {
                    const payload = JSON.parse(evt.data);
                    const cid = payload?.capture_id;
                    if (!cid) return;
                    setCaptures((list) => list.map((c) => c.capture_id === cid
                        ? { ...c, status: "failed", error: payload.error }
                        : c));
                } catch (_e) { /* ignore */ }
            });
            es.onerror = () => {
                // EventSource auto-retries the connection. Surface a
                // "Reconnecting…" state until either "phone_connected" /
                // "capture" resumes, or RECONNECT_HARD_MS elapses.
                setStatus((s) => {
                    if (s === "ended" || s === "expired") return s;
                    return "reconnecting";
                });
                if (!reconnectTimerRef.current) {
                    reconnectTimerRef.current = setTimeout(() => {
                        setStatus((s) => (s === "reconnecting" ? "offline" : s));
                    }, RECONNECT_HARD_MS);
                }
            };
        };

        openStream();

        // Some browsers close EventSource when the tab is hidden for a
        // long time — force a fresh connection on visibility return.
        const onVis = () => {
            if (document.visibilityState !== "visible") return;
            if (esRef.current && esRef.current.readyState === 2 /* CLOSED */) {
                esRef.current = null;
                openStream();
            }
        };
        document.addEventListener("visibilitychange", onVis);

        return () => {
            closed = true;
            document.removeEventListener("visibilitychange", onVis);
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
            if (esRef.current) { esRef.current.close(); esRef.current = null; }
        };
    }, [session?.session_id, mode, onFinishRef, onCloseRef]);

    // Delivery-mode durable poll — SSE is a nice-to-have but the
    // captures[] array in the DB is the source of truth. Polling every
    // 3 s (and immediately on visibility return) guarantees no capture
    // is ever missed by the desktop, even during SSE reconnects, tab
    // switches or Cloudflare hiccups.
    useEffect(() => {
        if (!open) return undefined;
        if (mode !== "delivery") return undefined;
        if (!session?.session_id) return undefined;
        const sid = session.session_id;
        let cancelled = false;

        const poll = async () => {
            if (cancelled) return;
            try {
                const { data } = await api.get(`/companion/captures/${sid}`);
                const list = data?.captures || [];
                // Reconcile the in-memory list with the durable server
                // state.  Rendering is driven from this list so refresh
                // / reconnect never loses a capture.
                setCaptures(list);
                for (const cap of list) {
                    const cid = cap.capture_id;
                    if (!cid) continue;
                    if (!seenCapturesRef.current.has(cid)) {
                        seenCapturesRef.current.add(cid);
                        setCaptureCount((c) => c + 1);
                        if (onCaptureRef.current) {
                            onCaptureRef.current(
                                { kind: "delivery", ...cap }, sid);
                        }
                    }
                    if (cap.status === "extracted"
                        && !notifiedExtractionRef.current.has(cid)
                        && onCaptureExtractedRef.current) {
                        notifiedExtractionRef.current.add(cid);
                        onCaptureExtractedRef.current(cap, sid);
                    }
                }
            } catch (_e) { /* transient — try again on next tick */ }
        };

        // Fire once immediately (covers refresh / re-open) then every 3 s.
        poll();
        pollRef.current = setInterval(poll, 3000);
        const onVis = () => {
            if (document.visibilityState === "visible") poll();
        };
        document.addEventListener("visibilitychange", onVis);

        return () => {
            cancelled = true;
            document.removeEventListener("visibilitychange", onVis);
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        };
    }, [open, mode, session?.session_id]);

    // Barcode-mode durable poll (Gap B defence).  Mirrors the delivery
    // poll but targets /companion/session/<sid>/events?status=pending so
    // an SSE reconnect gap can never strand accepted scans on the
    // desktop.  Dedupe via seenCapturesRef using the event_id so an
    // event delivered by BOTH SSE and the poll is only processed once.
    useEffect(() => {
        if (!open) return undefined;
        if (mode !== "barcode") return undefined;
        if (!session?.session_id) return undefined;
        const sid = session.session_id;
        let cancelled = false;

        const poll = async () => {
            if (cancelled) return;
            try {
                const { data } = await api.get(
                    `/companion/session/${sid}/events`,
                    { params: { status: "pending" } });
                const list = Array.isArray(data) ? data : [];
                for (const ev of list) {
                    const eid = ev.id || ev.event_id;
                    if (!eid || seenCapturesRef.current.has(eid)) continue;
                    seenCapturesRef.current.add(eid);
                    if (onCaptureRef.current) {
                        onCaptureRef.current({
                            kind: ev.kind, event_id: eid,
                            barcode: ev.barcode, quantity: ev.quantity,
                            unit: ev.unit, lookup: ev.lookup,
                            created_at: ev.created_at,
                        }, sid);
                    }
                }
            } catch (_e) { /* transient */ }
        };
        poll();
        pollRef.current = setInterval(poll, 3000);
        const onVis = () => {
            if (document.visibilityState === "visible") poll();
        };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            cancelled = true;
            document.removeEventListener("visibilitychange", onVis);
            if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        };
    }, [open, mode, session?.session_id]);
    const cancel = async () => {
        if (session?.session_id) {
            try { await api.post(`/companion/end/${session.session_id}?reason=cancelled`); }
            catch (_e) { /* ignore */ }
        }
        setSession(null);
        onClose && onClose();
    };

    const finish = async () => {
        if (session?.session_id) {
            try { await api.post(`/companion/end/${session.session_id}?reason=finished`); }
            catch (_e) { /* ignore */ }
        }
        setSession(null);
        onFinish && onFinish();
        onClose && onClose();
    };

    const regenerate = () => {
        setSession(null);
        setStatus("initialising");
        setError("");
        setCaptureCount(0);
        setCaptures([]);
        setSelectedIds(new Set());
        seenCapturesRef.current = new Set();
        notifiedExtractionRef.current = new Set();
        setRetryNonce((n) => n + 1);
    };

    const copyUrl = async () => {
        if (!session?.capture_url) return;
        try {
            await navigator.clipboard.writeText(fullUrl(session.capture_url));
            setCopied(true);
            toast.success(t("companion.linkCopied", "Link copied"));
            setTimeout(() => setCopied(false), 1800);
        } catch (_e) { /* ignore */ }
    };

    const captureUrl = useMemo(
        () => (session ? fullUrl(session.capture_url) : ""),
        [session]);
    const label = title || (mode === "delivery" ? t("companion.usePhoneCamera", "Use Phone Camera") : t("companion.usePhoneScanner", "Use Phone Scanner"));

    const showQr = session
        && status !== "error"
        && status !== "initialising"
        && status !== "ended"
        && status !== "expired";

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) cancel(); }}>
            <DialogContent className="max-w-md" data-testid="phone-companion-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Smartphone className="h-5 w-5" /> {label}
                    </DialogTitle>
                    <DialogDescription>
                        {mode === "delivery"
                            ? t("companion.scanQrDelivery", "Scan the QR with your phone to send a delivery-note photo.")
                            : t("companion.scanQrBarcode", "Scan the QR with your phone to scan barcodes into this count.")}
                    </DialogDescription>
                </DialogHeader>

                {status === "initialising" && (
                    <div className="py-6 text-center text-sm text-neutral-500"
                         data-testid="companion-initialising">
                        <RefreshCw className="h-4 w-4 mx-auto mb-2 animate-spin" />
                        {t("companion.preparingSession", "Preparing session…")}
                    </div>
                )}

                {status === "error" && (
                    <div className="py-6 text-center text-sm text-red-600" data-testid="companion-error">
                        <AlertTriangle className="h-5 w-5 mx-auto mb-2" />
                        <div className="mb-3">{error || t("companion.couldNotStart", "Could not start the phone session")}</div>
                        <button
                            onClick={regenerate}
                            data-testid="companion-retry-error"
                            className="h-9 px-4 rounded-md text-sm font-semibold bg-neutral-900 text-white hover:bg-neutral-800">
                            {t("companion.retryGenerateQr", "Retry / Generate new QR")}
                        </button>
                    </div>
                )}

                {(status === "expired" || status === "ended") && session && (
                    <div className="py-6 text-center text-sm" data-testid={`companion-${status}`}>
                        <AlertTriangle className="h-5 w-5 mx-auto mb-2 text-amber-600" />
                        <div className="mb-1 font-semibold">
                            {status === "expired" ? t("companion.sessionExpired", "Session expired") : t("companion.sessionEnded", "Session ended")}
                        </div>
                        <div className="text-neutral-500 mb-3">
                            {captureCount > 0
                                ? t("companion.capturesBeforeClose", { defaultValue: "{{count}} captures received before the session closed.", count: captureCount })
                                : t("companion.noCaptures", "No captures received.")}
                        </div>
                        <button
                            onClick={regenerate}
                            data-testid="companion-retry-ended"
                            className="h-9 px-4 rounded-md text-sm font-semibold bg-neutral-900 text-white hover:bg-neutral-800">
                            {t("companion.generateNewQr", "Generate new QR")}
                        </button>
                    </div>
                )}

                {showQr && (
                    <div className="space-y-4">
                        <div className="mx-auto w-fit rounded-md border border-neutral-200 p-3 bg-white"
                             data-testid="companion-qr">
                            <QRCodeSVG value={captureUrl} size={200} level="M" includeMargin={false} />
                        </div>

                        <div className="text-center">
                            <div className="text-xs uppercase tracking-wide text-neutral-500">
                                {t("companion.orOpenOnPhone", "Or open on your phone")}
                            </div>
                            <div className="mt-1 flex items-center justify-center gap-2">
                                <code className="text-xs font-mono px-2 py-1 rounded bg-neutral-100 border border-neutral-200 max-w-[280px] truncate"
                                      data-testid="companion-short-url" title={captureUrl}>
                                    {shortForDisplay(captureUrl, session.short_code)}
                                </code>
                                <button onClick={copyUrl} data-testid="companion-copy-url"
                                    aria-label={t("companion.copyLink", "Copy link")}
                                    className="h-7 w-7 grid place-items-center rounded hover:bg-neutral-100 text-neutral-700">
                                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                                </button>
                            </div>
                            <div className="mt-1 text-[11px] text-neutral-400">{t("companion.code", "code")} · {session.short_code}</div>
                        </div>

                        <StatusPill status={status} count={captureCount} />

                        {mode === "delivery" && captures.length > 0 && (
                            <CapturesPanel
                                sessionId={session?.session_id}
                                captures={captures}
                                selectedIds={selectedIds}
                                onToggleSelected={(cid) => {
                                    setSelectedIds((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(cid)) next.delete(cid);
                                        else next.add(cid);
                                        return next;
                                    });
                                }}
                                onContinueSingle={(cap) => {
                                    if (onContinueToReview) {
                                        onContinueToReview([cap], session?.session_id);
                                        setSelectedIds(new Set());
                                    }
                                }}
                                onContinueSelected={() => {
                                    const picked = captures.filter((c) =>
                                        selectedIds.has(c.capture_id)
                                        && c.status === "extracted"
                                        && c.extraction);
                                    if (picked.length === 0) return;
                                    if (onContinueToReview) {
                                        onContinueToReview(picked, session?.session_id);
                                        setSelectedIds(new Set());
                                    }
                                }} />
                        )}

                        {(status === "offline" || status === "reconnecting") && (
                            <div className="text-center">
                                <button onClick={regenerate}
                                    data-testid="companion-reconnect"
                                    className="text-xs font-semibold text-neutral-700 underline underline-offset-2 hover:text-neutral-900">
                                    {t("companion.notWorking", "Not working? Generate a new QR")}
                                </button>
                            </div>
                        )}

                        <div className="flex justify-end gap-2 pt-1">
                            <button onClick={cancel} data-testid="companion-cancel"
                                className="h-9 px-4 inline-flex items-center gap-1 rounded-md text-sm border border-neutral-300 bg-white hover:bg-neutral-50">
                                <XIcon className="h-4 w-4" /> {t("common.cancel", "Cancel")}
                            </button>
                            <button onClick={finish} data-testid="companion-finish"
                                disabled={captureCount === 0 && mode === "barcode"}
                                className="h-9 px-4 inline-flex items-center gap-1 rounded-md text-sm font-semibold bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed">
                                <Check className="h-4 w-4" /> {t("companion.finish", "Finish")}
                            </button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function fullUrl(pathOrUrl) {
    if (!pathOrUrl) return "";
    if (/^https?:/i.test(pathOrUrl)) return pathOrUrl;
    const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const base = backend.replace(/\/api\/?$/, "");
    return `${base}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

function shortForDisplay(url, code) {
    try {
        const u = new URL(url);
        return `${u.host}/companion?t=${code || "…"}`;
    } catch (_e) {
        return url;
    }
}

function CapturesPanel({ sessionId, captures, selectedIds,
                         onToggleSelected, onContinueSingle,
                         onContinueSelected }) {
    const { t } = useTranslation();
    if (!sessionId) return null;
    const backend = process.env.REACT_APP_BACKEND_URL;
    const selectedCount = Array.from(selectedIds || []).filter((id) =>
        captures.some((c) => c.capture_id === id
                              && c.status === "extracted"
                              && c.extraction)).length;
    return (
        <div className="rounded-md border border-neutral-200 bg-neutral-50 p-2"
             data-testid="companion-captures-panel">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 px-1 pb-1 flex items-center justify-between">
                <span>{t("companion.photosFromPhone", "Photos from phone")} · {captures.length}</span>
                {selectedCount > 1 && (
                    <button
                        data-testid="companion-continue-selected-btn"
                        onClick={onContinueSelected}
                        className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 underline">
                        {t("companion.continueToReview", { defaultValue: "Continue to Review ({{n}})", n: selectedCount })}
                    </button>
                )}
            </div>
            <div className="max-h-56 overflow-y-auto space-y-2 pr-1">
                {captures.map((c, idx) => (
                    <CaptureTile
                        key={c.capture_id}
                        index={idx}
                        sessionId={sessionId}
                        capture={c}
                        backend={backend}
                        selected={selectedIds?.has(c.capture_id) || false}
                        onToggleSelected={onToggleSelected}
                        onContinueSingle={onContinueSingle} />
                ))}
            </div>
        </div>
    );
}

function CaptureTile({ index, sessionId, capture, backend,
                       selected, onToggleSelected, onContinueSingle }) {
    const { t } = useTranslation();
    const isImage = (capture.file_mime || "").startsWith("image/");
    const src = isImage && backend
        ? `${backend}/api/companion/captures/${sessionId}/${capture.capture_id}/file`
        : null;
    const statusMap = {
        uploaded:   { cls: "bg-neutral-100 text-neutral-700 border-neutral-200",
                       label: t("companion.status.uploaded", "Uploaded ✓") },
        extracting: { cls: "bg-blue-50 text-blue-700 border-blue-200",
                       label: t("companion.status.extracting", "AI reading…"), Icon: RefreshCw },
        extracted:  { cls: "bg-emerald-50 text-emerald-800 border-emerald-200",
                       label: t("companion.status.extracted", "Extracted"), Icon: Check },
        failed:     { cls: "bg-red-50 text-red-700 border-red-200",
                       label: t("companion.status.failed", "AI failed — photo kept"), Icon: AlertTriangle },
    };
    const s = statusMap[capture.status] || statusMap.uploaded;
    const StatusIcon = s.Icon;
    const supplier = capture.extraction_summary?.supplier;
    const canContinue = capture.status === "extracted" && !!capture.extraction;
    return (
        <div className="flex gap-2 items-center bg-white rounded-md border border-neutral-200 p-2"
             data-testid={`companion-capture-tile-${index}`}>
            {canContinue && onToggleSelected && (
                <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggleSelected(capture.capture_id)}
                    className="h-4 w-4 accent-emerald-600 shrink-0"
                    aria-label={t("companion.selectPhoto", { defaultValue: "Select photo {{n}}", n: index + 1 })}
                    data-testid={`companion-capture-select-${index}`} />
            )}
            <div className="w-12 h-12 rounded overflow-hidden bg-neutral-100 grid place-items-center shrink-0">
                {src ? (
                    <img src={src} alt={t("companion.deliveryPhotoN", { defaultValue: "Delivery photo {{n}}", n: index + 1 })}
                         className="w-full h-full object-cover"
                         loading="lazy" />
                ) : (
                    <span className="text-[10px] font-semibold text-neutral-500">PDF</span>
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold truncate"
                     data-testid={`companion-capture-name-${index}`}>
                    {t("companion.photoN", { defaultValue: "Photo {{n}}", n: index + 1 })} · {capture.file_name || t("companion.capture", "capture")}
                </div>
                <div className="mt-0.5 inline-flex items-center gap-1">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-semibold ${s.cls}`}
                          data-testid={`companion-capture-status-${index}`}>
                        {s.Icon && <StatusIcon className="h-3 w-3" />}
                        {s.label}
                    </span>
                    {supplier?.name && (
                        <span className="text-[10px] text-neutral-500 truncate">
                            · {supplier.name}
                        </span>
                    )}
                </div>
                {capture.status === "failed" && capture.error && (
                    <div className="mt-0.5 text-[10px] text-red-600 truncate"
                         title={translateErrorText(capture.error)}
                         data-testid={`companion-capture-error-${index}`}>
                        {translateErrorText(capture.error)}
                    </div>
                )}
            </div>
            {canContinue && onContinueSingle && (
                <button
                    onClick={() => onContinueSingle(capture)}
                    data-testid={`companion-capture-continue-${index}`}
                    className="shrink-0 h-8 px-2 rounded-md bg-emerald-600 text-white text-[11px] font-semibold hover:bg-emerald-700 inline-flex items-center gap-1">
                    <ArrowRight className="h-3 w-3" />
                    {t("companion.review", "Review")}
                </button>
            )}
        </div>
    );
}

function StatusPill({ status, count }) {
    const { t } = useTranslation();
    const map = {
        waiting:      { cls: "bg-neutral-100 text-neutral-700 border-neutral-200",
                         label: t("companion.pill.waiting", "Waiting for phone…"), Icon: RefreshCw },
        connected:    { cls: "bg-emerald-50 text-emerald-800 border-emerald-200",
                         label: t("companion.pill.connected", "Phone Connected"), Icon: Check },
        reconnecting: { cls: "bg-amber-50 text-amber-800 border-amber-200",
                         label: t("companion.pill.reconnecting", "Reconnecting…"), Icon: RefreshCw },
        offline:      { cls: "bg-amber-50 text-amber-800 border-amber-200",
                         label: t("companion.pill.offline", "Phone Offline — check network"), Icon: WifiOff },
        expired:      { cls: "bg-red-50 text-red-800 border-red-200",
                         label: t("companion.sessionExpired", "Session expired"), Icon: AlertTriangle },
        ended:        { cls: "bg-neutral-100 text-neutral-500 border-neutral-200",
                         label: t("companion.sessionEnded", "Session ended") },
    };
    const m = map[status] || map.waiting;
    const Icon = m.Icon;
    const spin = status === "waiting" || status === "reconnecting";
    return (
        <div className={`mx-auto w-fit inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${m.cls}`}
             data-testid={`companion-status-${status}`}>
            {Icon && <Icon className={`h-3.5 w-3.5 ${spin ? "animate-spin" : ""}`} />}
            <span>{m.label}</span>
            {count > 0 && (
                <span className="ml-2 rounded-full bg-white/70 border border-current/20 px-2 py-0.5 text-[10px] font-bold">
                    {t("companion.capturesCount", { defaultValue: "{{count}} captures", count })}
                </span>
            )}
        </div>
    );
}
